import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./learnHome.html";
import { WireStore, selectParams } from '@oneappexchange/lwc-wired';
import { LEARN_BASE_ROUTE } from '~/modules/routes';
import { initialPageRenderedMetricsCheck } from '~/modules/services/metricsService';
import { WireI18n, i18nStore } from '~/modules/stores/i18nStore';
import { navigate, routerStore } from '~/modules/stores/routerStore';
import { selectContentTypes, selectHydratedContentItems, selectHydratedContentItemsByContentType, selectPersonas, wordPressStore } from '~/modules/stores/wordpress_store';
import { images } from '~/modules/utilities/assetUtilities';
const POSTS_PER_PAGE = 12;
class LearnHome extends LightningElement {
  constructor(...args) {
    super(...args);
    this.contentData = [];
    this.contentTypesData = [];
    this.personasData = [];
    this.currentPage = 1;
    this.errorEmptyStateImageUrl = images.errorEmptyState;
    this.hasOverallError = false;
  }
  i18nCallback(i18n) {
    this.labels = i18n.learn;
    this.language = i18nStore.getState().currentLanguage;
  }
  routeChanged(params) {
    wordPressStore.setLanguage(this.language);

    // Always pre-populate the filters (this is a no-op if it's already populated)
    wordPressStore.fetchContentTypes();
    wordPressStore.fetchPersonas();
    if (params?.contentType) {
      this.filterContentType = params.contentType;
    } else {
      this.filterContentType = undefined;
    }
    if (params?.persona) {
      this.filterPersona = params.persona;
    } else {
      this.filterPersona = undefined;
    }
    if (params?.page) {
      this.currentPage = Number(params.page);
    } else {
      this.currentPage = 1;
    }

    // This handles client side navigation, when params are changing, but contentTypes are already cached
    this.updatePostData();
  }
  wordPressContentUpdated(state) {
    this.updatePostData(state);
  }
  updatePostData(state = wordPressStore.getState()) {
    this.contentTypesData = selectContentTypes(state);
    this.personasData = selectPersonas(state);
    this.hasOverallError = state.personasOverallError || state.contentTypesOverallError;

    // Do not attempt to fetch posts until the content types and personas are ready
    if (this.contentTypesData.length === 0 || this.personasData.length === 0) {
      return;
    }

    // After an initial successful fetch, we should load 3 posts for each contentType that have them available (or
    // POSTS_PER_PAGE if filtered). These are no-ops if the data is already fetching or available.
    if (this.filterActive) {
      wordPressStore.fetchPostsWithFilter(this.currentPage, POSTS_PER_PAGE, this.filterContentType, this.filterPersona, this.language);

      // Due to a updatePostData getting called from both routeChanged and wordPressContentUpdated,
      // fetchPostsWithFilter will trigger an immediate update here due to load state. To prevent the original
      // routeChanged from having old state, refresh it now.
      state = wordPressStore.getState();
      const currentFilteredData = selectHydratedContentItems(state, this.filterContentType, this.filterPersona);
      if (currentFilteredData?.hydratedItems) {
        this.contentData = [currentFilteredData];
      } else {
        this.contentData = [];
      }
    } else {
      this.contentData = selectHydratedContentItemsByContentType(state);
      this.contentTypesData.forEach(contentType => {
        if (contentType.count > 0) {
          wordPressStore.fetchPostsWithFilter(this.currentPage, 3, contentType.slug, undefined, this.language);
        }
      });
    }
  }
  get filterActive() {
    return !!this.filterContentType || !!this.filterPersona;
  }
  get displayEmptyState() {
    if (this.filterActive && this.contentData.length === 1) {
      const currentFilter = this.contentData[0];
      return !currentFilter.isLoading && !currentFilter.isError && currentFilter.hydratedItems?.length === 0;
    }
    return false;
  }
  get displayErrorState() {
    if (this.hasOverallError) {
      return true;
    }
    if (this.filterActive) {
      if (this.contentData.length === 1) {
        const currentFilter = this.contentData[0];
        return currentFilter.isError;
      }
    } else if (this.contentData.length > 1) {
      // Detect if all sections failed to load
      for (const section of this.contentData) {
        if (section.isLoading || !section.isError) {
          return false;
        }
      }
      return true;
    }
    return false;
  }
  get visiblePostSections() {
    if (this.filterActive) {
      const startPost = (this.currentPage - 1) * POSTS_PER_PAGE;
      return this.contentData.map(section => ({
        ...section,
        hydratedItems: section.hydratedItems.slice(startPost, startPost + POSTS_PER_PAGE)
      }));
    }
    return this.contentData.map(section => ({
      ...section,
      hydratedItems: section.hydratedItems.slice(0, 3)
    }));
  }
  get showPagination() {
    if (this.filterActive) {
      if (this.contentData.length > 0) {
        return this.contentData[0].totalItems > POSTS_PER_PAGE;
      }
    }
    return false;
  }
  getUrlForFilters(contentType = this.filterContentType, persona = this.filterPersona) {
    const filterParts = [];
    if (contentType) {
      filterParts.push(`contentType=${contentType}`);
    }
    if (persona) {
      filterParts.push(`persona=${persona}`);
    }
    if (filterParts.length) {
      return `/${LEARN_BASE_ROUTE}?${filterParts.join('&')}`;
    }
    return `/${LEARN_BASE_ROUTE}`;
  }
  getUrlForPage(page) {
    const filterTargetUrl = this.getUrlForFilters();
    if (page === 1) {
      return filterTargetUrl;
    }
    return `${filterTargetUrl}&page=${page}`;
  }
  get pageList() {
    if (this.contentData.length > 0) {
      const totalPages = Math.ceil(this.contentData[0].totalItems / POSTS_PER_PAGE);
      return Array.from({
        length: totalPages
      }, (_, i) => ({
        url: this.getUrlForPage(i + 1),
        index: i + 1,
        isCurrent: i + 1 === this.currentPage
      }));
    }
    return [];
  }
  get contentTypes() {
    return this.contentTypesData.map(contentType => ({
      slug: contentType.slug,
      name: contentType.name,
      singular: contentType?.labels?.singular || contentType.name,
      plural: contentType?.labels?.plural || contentType.name,
      selected: contentType.slug === this.filterContentType
    }));
  }
  get personas() {
    return this.personasData.map(persona => ({
      slug: persona.slug,
      name: persona.name,
      singular: persona?.labels?.singular || persona.name,
      plural: persona?.labels?.plural || persona.name,
      selected: persona.slug === this.filterPersona
    }));
  }
  get filters() {
    return [{
      type: 'contentType',
      label: this.labels.filterContentTypeLabel,
      options: this.contentTypes
    }, {
      type: 'persona',
      label: this.labels.filterRoleLabel,
      options: this.personas
    }];
  }
  filterUpdated(event) {
    if (event.detail.filterType === 'contentType') {
      navigate(this.getUrlForFilters(event.detail.value, this.filterPersona));
    } else if (event.detail.filterType === 'persona') {
      navigate(this.getUrlForFilters(this.filterContentType, event.detail.value));
    }
  }
  pageClicked(event) {
    if (event.detail.pageUrl) {
      // We must trim down the URL to client side navigate correctly
      const trimmedUrl = event.detail.pageUrl.substring(event.detail.pageUrl.indexOf(LEARN_BASE_ROUTE));
      navigate('/' + trimmedUrl);
    }
  }
  renderedCallback() {
    if (this.visiblePostSections.length > 0) {
      initialPageRenderedMetricsCheck();
    }
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(LearnHome, {
  track: {
    contentData: 1,
    contentTypesData: 1,
    personasData: 1
  },
  wire: {
    i18nCallback: {
      adapter: WireI18n,
      method: 1,
      config: function ($cmp) {
        return {};
      }
    },
    routeChanged: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: routerStore,
          selector: selectParams
        };
      }
    },
    wordPressContentUpdated: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: wordPressStore
        };
      }
    }
  },
  fields: ["currentPage", "errorEmptyStateImageUrl", "hasOverallError"]
});
const __lwc_component_class_internal = _registerComponent(LearnHome, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;